import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useModalStore } from '../../shared/store/portal/modalStore';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import { useProjectListActions, useProjectListState } from '../../shared/hooks/useApi/useProjectList';
import { IndexOrderData, ProjectElementDataWithFormation } from '../../shared/dto/EntityRepository';
import { CareerType, ModalType, TextBlockType } from '../../shared/enum/EnumRepository';
import { useProjectInfoActions, useProjectInfoState } from '../../shared/hooks/useApi/useProjectInfo';
import { useProjectElementActions, useProjectElementState } from '../../shared/hooks/useApi/useProjectElement';
import { ProjectElementType } from '../../shared/enum/EnumRepository';
import { useCareer } from '../../shared/hooks/useApi/useCareer';
import { ReorderReq, UpdateReorderListReq } from '../../shared/dto/ReqDtoRepository';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import {
  useCanShowProjectDetail,
  useCanShowProjectElementList,
  useCanShowProjectInfoList,
  useCanShowProjectList,
} from '../../shared/hooks/useProjectVisibility';
import { useCanShowCareerList } from '../../shared/hooks/useCareerVisibility';
import { useCanShowGroupMemberList } from '../../shared/hooks/useGroupMemberVisibility';
import { slideInFromLeftWeighted } from '../../shared/component/Animation';
import { useTextViewStore } from '../../shared/store/TextViewStore';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useTextBlock } from '../../shared/hooks/useApi/useTextBlock';
import { useWorkDetailActions } from '../../shared/hooks/useApi/useWorkDetail';
import { getCareerTypeTitle, resolveCareerSections } from '../about/CareerData';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getPageMessage } from '../../shared/constants/pageMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { paperTextureModal } from '../../shared/component/paperTexture';
import IndexCard from './IndexCard';
import pdfIcon from '../../asset/icon/pdf.png';
import { getFormationTypeLabel, resolveFormationSlotImage } from '../../shared/util/formationUtils';
import { getProjectInfoDisplayName, getProjectInfoDisplayText } from '../../shared/util/projectInfo';
import { useGroupMember } from '../../shared/hooks/useApi/useGroupMember';
import {
  useAboutPageSettingActions,
  useAboutPageSettingState,
} from '../../shared/hooks/useApi/useAboutPageSetting';
import { useCanShowAboutPageSetting } from '../../shared/hooks/useAboutPageSettingVisibility';
import { mergeActiveCareerSectionOrder } from '../../shared/util/careerSectionOrder';
import { useProjectInfoListStore } from '../../shared/store/projectInfoStore';

// Auto-scroll configuration
const AUTO_SCROLL_SPEED = 7; // 스크롤 속도 (픽셀 단위, 값이 클수록 빠름)

const Indexing: React.FC = () => {
  const { standardModal, isClosing, clearModal } = useModalStore();
  const { projects } = useProjectListState();
  const { reorderProject } = useProjectListActions();
  const { project } = useProjectDetailState();
  const canShowProjectDetail = useCanShowProjectDetail();
  const canShowInfoList = useCanShowProjectInfoList();
  const canShowElementList = useCanShowProjectElementList();
  const canShowProjectList = useCanShowProjectList();
  const canShowCareerList = useCanShowCareerList();
  const canShowGroupMemberList = useCanShowGroupMemberList();
  const { projectInfoList } = useProjectInfoState();
  const updateProjectInfo = useProjectInfoListStore((s) => s.updateProjectInfo);
  const { reorderProjectInfo } = useProjectInfoActions();
  const { projectElementList } = useProjectElementState();
  const { reorderProjectElement } = useProjectElementActions();
  const { careerList, reorderCareer } = useCareer();
  const { aboutPageSetting } = useAboutPageSettingState();
  const { updateCareerSectionOrder } = useAboutPageSettingActions();
  const canShowAboutPageSetting = useCanShowAboutPageSetting();
  const { reorderTextBlock } = useTextBlock();
  const { reorderWorkDetail } = useWorkDetailActions();
  const { activeText, activeTextBlockList } = useTextViewStore();
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const activeWorkDetailList = useWorkViewStore((s) => s.activeWorkDetailList);
  const { groupMemberList, reorderGroupMembers } = useGroupMember();
  const [orderedDataList, setOrderedDataList] = useState<IndexOrderData[]>([]);
  const [grabbedData, setGrabbedData] = useState<IndexOrderData | null>(null);
  const [selectedCard, setSelectedCard] = useState<string[]>([]);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<number | null>(null);
  const dragMovingIdsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!standardModal) return;
    if (standardModal.modalType !== ModalType.INDEXING) return;
    createOrderedContent();
  }, [standardModal]);

  const createOrderedContent = () => {
    setSelectedCard([]);
    switch (standardModal?.title) {
      case "Project":
        const orderedProjects = projects.map((project, index) => (
          {
            index,
            id: project.id,
            mainText: project.title,
            subText: project.projectYear ? project.projectYear.toString() : ''
          }
        ));
        setOrderedDataList(orderedProjects);
        break;
      case "Info":
        const orderedInfos = projectInfoList.map((info, index) => (
          {
            index,
            id: info.id,
            mainText: getProjectInfoDisplayName(info.customName),
            subText: getProjectInfoDisplayText(info.customValues)
          }
        ));
        setOrderedDataList(orderedInfos);
        break;
      case "Info Value": {
        const targetInfo = projectInfoList.find(
          (info) => info.id === standardModal.value,
        );
        const orderedValues = (targetInfo?.customValues ?? []).map((value, index) => ({
          index,
          id: String(index),
          mainText: value.text || '(No value)',
          subText: value.venueId != null ? 'Venue' : value.url ? 'URL' : '',
        }));
        setOrderedDataList(orderedValues);
        break;
      }
      case "Element":
        const orderedElements = projectElementList.map((element, index) => {
          switch (element.projectElementType) {
            case ProjectElementType.WORK: {
              return {
                index,
                id: element.id,
                mainText: element.work.title,
                subText: "Work",
                originUrl: element.work.uploadFile?.originUrl
              };
            }
            case ProjectElementType.DETAIL:
              return {
                index,
                id: element.id,
                mainText: element.workDetail.description,
                subText: "Detail Cut",
                originUrl: element.workDetail.uploadFile?.originUrl
              };
            case ProjectElementType.TEXTBOX:
              return {
                index,
                id: element.id,
                mainText: element.textBox.content,
                subText: "Text Box"
              };
            case ProjectElementType.DOCUMENT:
              return {
                index,
                id: element.id,
                mainText: element.document.description,
                subText: "Visual Aid",
                originUrl: element.document.uploadFile?.originUrl
              };
            case ProjectElementType.PDF:
              return {
                index,
                id: element.id,
                mainText: element.pdfAsset.description || "PDF",
                subText: "PDF",
                originUrl: pdfIcon
              };
            case ProjectElementType.TEXT:
              return {
                index,
                id: element.id,
                mainText: element.text.title,
                subText: "Text"
              };
            case ProjectElementType.DIVIDER:
              return {
                index,
                id: element.id,
                mainText: "Divider",
                subText: ""
              };
            case ProjectElementType.FORMATION: {
              const formationElement = element as ProjectElementDataWithFormation;
              const firstItem = formationElement.formation.items[0];
              const resolvedImage = firstItem ? resolveFormationSlotImage(firstItem) : null;
              return {
                index,
                id: element.id,
                mainText: getFormationTypeLabel(formationElement.formation.formationType),
                subText: "Formation",
                originUrl: resolvedImage?.originUrl,
              };
            }
            default:
              return null;
          }
        }).filter(item => item !== null) as IndexOrderData[];

        setOrderedDataList(orderedElements);
        break;
      case "Text":
        const orderedTexts = activeTextBlockList.map((tb, index) => {
          switch (tb.type) {
            case TextBlockType.IMAGE:
              return {
                index,
                id: tb.id,
                mainText: tb.caption,
                subText: tb.type,
                originUrl: tb.uploadFile?.originUrl
              };
            default:
              return {
                index,
                id: tb.id,
                mainText: tb.textContent,
                subText: tb.type,
              };
          }
        });
        setOrderedDataList(orderedTexts);
        break;
      case "Detail Cut":
        const orderedDetails = activeWorkDetailList.map((wd, index) => (
          {
            index,
            id: wd.id,
            mainText: wd.description || '(No description)',
            subText: 'Detail Cut',
            originUrl: wd.uploadFile?.originUrl
          }
        ));
        setOrderedDataList(orderedDetails);
        break;
      case "Career":
        const filteredCareers = careerList.filter((each) => each.careerType === standardModal.value);
        const orderedCareers = filteredCareers.map((career, index) => (
          {
            index,
            id: career.id,
            mainText: career.content,
            subText: career.startYear
          }
        ));
        setOrderedDataList(orderedCareers);
        break;
      case "Career Section":
        const activeCareerTypes = new Set(
          careerList.map((career) => career.careerType),
        );
        const orderedCareerSections = resolveCareerSections(
          aboutPageSetting?.careerSectionOrder,
        )
          .filter((section) => activeCareerTypes.has(section.type))
          .map((section, index) => ({
            index,
            id: section.type,
            mainText: section.title,
            subText: '',
          }));
        setOrderedDataList(orderedCareerSections);
        break;
      case "Group Member":
        const orderedGroupMembers = groupMemberList.map((member, index) => ({
          index,
          id: member.id,
          mainText: member.name,
          subText: member.role,
        }));
        setOrderedDataList(orderedGroupMembers);
        break;
    }
  };

  const toggleSelectedCard = (id: string) => {
    setSelectedCard((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const clearDragState = () => {
    setGrabbedData(null);
    dragMovingIdsRef.current = [];
  };

  const handleDragStart = (data: IndexOrderData) => {
    const orderedIdsInSelection = orderedDataList
      .filter((item) => selectedCard.includes(item.id))
      .map((item) => item.id);
    const isGroupDrag =
      orderedIdsInSelection.length > 1 && selectedCard.includes(data.id);
    dragMovingIdsRef.current = isGroupDrag ? orderedIdsInSelection : [data.id];
    setGrabbedData(data);
  };

  const handleDropSpaceDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDropSpaceDragLeave = () => {
    setDragOverIndex(null);
  };

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current !== null) {
      cancelAnimationFrame(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  const startAutoScroll = (direction: 'up' | 'down') => {
    stopAutoScroll();
    
    const scroll = () => {
      if (!contentContainerRef.current) return;
      
      const scrollAmount = direction === 'up' ? -AUTO_SCROLL_SPEED : AUTO_SCROLL_SPEED;
      contentContainerRef.current.scrollTop += scrollAmount;
      
      autoScrollIntervalRef.current = requestAnimationFrame(scroll);
    };
    
    autoScrollIntervalRef.current = requestAnimationFrame(scroll);
  };

  const handleContentDragOver = (e: React.DragEvent) => {
    if (!grabbedData || !contentContainerRef.current) return;
    
    const container = contentContainerRef.current;
    const rect = container.getBoundingClientRect();
    const mouseY = e.clientY;
    const scrollThreshold = 50; // 경계로부터 50px 이내에서 자동 스크롤 시작
    
    const distanceFromTop = mouseY - rect.top;
    const distanceFromBottom = rect.bottom - mouseY;
    
    if (distanceFromTop < scrollThreshold && container.scrollTop > 0) {
      startAutoScroll('up');
    } else if (distanceFromBottom < scrollThreshold && 
      container.scrollTop < container.scrollHeight - container.clientHeight) {
      startAutoScroll('down');
    } else {
      stopAutoScroll();
    }
  };

  const handleContentDragLeave = () => {
    stopAutoScroll();
  };

  useEffect(() => {
    if (!grabbedData) {
      stopAutoScroll();
    }
    
    return () => {
      stopAutoScroll();
    };
  }, [grabbedData]);

  const handleDrop = (dropIndex: number) => {
    if (!grabbedData) return;

    const movingIds =
      dragMovingIdsRef.current.length > 0
        ? dragMovingIdsRef.current
        : [grabbedData.id];
    const movingSet = new Set(movingIds);
    const blockOrder = orderedDataList.filter((item) => movingSet.has(item.id));
    const without = orderedDataList.filter((item) => !movingSet.has(item.id));

    let insertAt = 0;
    for (let i = 0; i < dropIndex; i++) {
      if (!movingSet.has(orderedDataList[i].id)) insertAt++;
    }

    const updatedList = [
      ...without.slice(0, insertAt),
      ...blockOrder,
      ...without.slice(insertAt),
    ].map((item, idx) => ({
      ...item,
      index: idx,
    }));

    setOrderedDataList(updatedList);
    if (movingIds.length > 1) {
      setSelectedCard([]);
    }
    clearDragState();
    setDragOverIndex(null);
  };

  const handleSubmit = async () => {
    const reorderReqList: ReorderReq[] = orderedDataList.map((each) => ({
      index: each.index,
      id: each.id
    }));

    switch (standardModal?.title) {
      case "Project":
        const updateProjectReorderListReq: UpdateReorderListReq = {
          id: "",
          reorderReqList: reorderReqList
        };
        reorderProject(updateProjectReorderListReq);
        break;
      case "Info":
        if (!project) return;
        const updateProjectInfoReorderListReq: UpdateReorderListReq = {
          id: project.id,
          reorderReqList: reorderReqList
        };
        reorderProjectInfo(updateProjectInfoReorderListReq);
        break;
      case "Info Value": {
        const targetInfo = projectInfoList.find(
          (info) => info.id === standardModal.value,
        );
        if (!targetInfo) return;
        const reorderedValues = orderedDataList.map(
          (item) => targetInfo.customValues[Number(item.id)],
        );
        if (reorderedValues.some((value) => value == null)) return;
        updateProjectInfo(targetInfo.id, { customValues: reorderedValues });
        break;
      }
      case "Element":
        if (!project) return;
        const updateProjectElementReorderListReq: UpdateReorderListReq = {
          id: project.id,
          reorderReqList: reorderReqList
        };
        reorderProjectElement(updateProjectElementReorderListReq);
        break;
      case "Text":
        if (!activeText) return;
        const updateTextReorderListReq: UpdateReorderListReq = {
          id: activeText.id,
          reorderReqList: reorderReqList
        };
        reorderTextBlock(updateTextReorderListReq);
        break;
      case "Detail Cut":
        if (!activeWork) return;
        const updateWorkDetailReorderListReq: UpdateReorderListReq = {
          id: activeWork.id,
          reorderReqList: reorderReqList
        };
        reorderWorkDetail(updateWorkDetailReorderListReq);
        break;
      case "Career":
        if (!standardModal.value) return;

        const careerMaxIndex = reorderReqList.length - 1;
        const updateCareerReorderListReq: UpdateReorderListReq = {
          id: standardModal.value,
          reorderReqList: reorderReqList.map((req) => ({
            ...req,
            index: careerMaxIndex - req.index,
          })),
        };
        reorderCareer(updateCareerReorderListReq);
        break;
      case "Career Section": {
        const reorderedActiveTypes = orderedDataList.map(
          (item) => item.id as CareerType,
        );
        const didUpdate = await updateCareerSectionOrder({
          careerSectionOrder: mergeActiveCareerSectionOrder(
            aboutPageSetting?.careerSectionOrder,
            reorderedActiveTypes,
          ),
        });
        if (!didUpdate) return;
        break;
      }
      case "Group Member":
        reorderGroupMembers({
          id: "",
          reorderReqList,
        });
        break;
    }
    clearModal(400);
  };

  const isIndexingBlocked =
    (standardModal?.title === 'Info' && !(canShowProjectDetail && canShowInfoList)) ||
    (standardModal?.title === 'Info Value' && !(canShowProjectDetail && canShowInfoList)) ||
    (standardModal?.title === 'Element' && !(canShowProjectDetail && canShowElementList)) ||
    (standardModal?.title === 'Project' && !canShowProjectList) ||
    (standardModal?.title === 'Career' && !canShowCareerList) ||
    (standardModal?.title === 'Career Section' &&
      !(canShowCareerList && canShowAboutPageSetting)) ||
    (standardModal?.title === 'Group Member' && !canShowGroupMemberList);

  if (isIndexingBlocked) return null;

  return (
    <LeftThinModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <IndexingComp>
        <TitleContainer>
          <Title>{getModalMessage('REORDER_TITLE', { title: standardModal?.title || '' })}</Title>
          {standardModal?.title === "Career" &&
            <SubTitle>{getCareerTypeTitle(standardModal?.value as CareerType)}</SubTitle>
          }
        </TitleContainer>
        {orderedDataList.length > 0 ? (
          <ContentContainer
            ref={contentContainerRef}
            onDragOver={handleContentDragOver}
            onDragLeave={handleContentDragLeave}
          >
            {orderedDataList.map((each, index) => (
              <React.Fragment key={each.id}>
                <DropSpace
                  onDrop={() => handleDrop(index)}
                  onDragOver={(e) => handleDropSpaceDragOver(e, index)}
                  onDragLeave={handleDropSpaceDragLeave}
                  $isActive={dragOverIndex === index}
                />
                <IndexCard
                  data={each}
                  isDragging={
                    grabbedData !== null &&
                    dragMovingIdsRef.current.includes(each.id)
                  }
                  isSelected={selectedCard.includes(each.id)}
                  onDragStart={handleDragStart}
                  onDragEnd={clearDragState}
                  onToggleSelect={toggleSelectedCard}
                />
              </React.Fragment>
            ))}
            <DropSpace
              onDrop={() => handleDrop(orderedDataList.length)}
              onDragOver={(e) => handleDropSpaceDragOver(e, orderedDataList.length)}
              onDragLeave={handleDropSpaceDragLeave}
              $isActive={dragOverIndex === orderedDataList.length}
            />
            <DropSpaceLast
              onDrop={() => handleDrop(orderedDataList.length)}
              onDragOver={(e) => handleDropSpaceDragOver(e, orderedDataList.length)}
              onDragLeave={handleDropSpaceDragLeave}
            />
          </ContentContainer>
        ) : (
        <NoContentContainer>
          {getPageMessage('NO_ITEMS_TO_REORDER')}
        </NoContentContainer>
        )}
        <ButtonContainer>
          <HeadlessBtn
            value={getBtnName('CHANGE')}
            handleClick={handleSubmit}
            StyledBtn={BlackFitBtn}
          />
          <HeadlessBtn
            value={getBtnName('CANCEL')}
            handleClick={() => clearModal(400)}
            StyledBtn={PaperBlurFitBtn}
          />
        </ButtonContainer>
      </IndexingComp>
    </LeftThinModalFrame>
  );
};

export const LeftThinModalFrame = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  left: 2.5vw;
  width: calc(20vw);
  min-width: 300px;
  max-width: 300px;
  height: 85vh;
  
  padding: 20px;

  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) =>
    $isClosing
      ? 'translateX(-100%)'
      : 'translateX(0)'
  };

  transition: all 0.6s ease-out;
  ${paperTextureModal}
  ${slideInFromLeftWeighted}
`;

const IndexingComp = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const TitleContainer = styled.div`
  width: 100%;
  height: 5vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const Title = styled.div`
  display: flex;
  justify-content: flex-start;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const SubTitle = styled.div`
  margin-top: 6px;
  text-decoration: underline;
  font-style: italic;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_04};
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 65vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NoContentContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 10px 0px;
`;

const DropSpace = styled.div<{ $isActive?: boolean }>`
  width: 100%;
  height: ${({ $isActive }) => $isActive ? '60px' : '8px'};
  min-height: ${({ $isActive }) => $isActive ? '30px' : '8px'};
  margin: 2px 0;
  border-radius: 2px;
  transition: all 0.4s;
  background-color: transparent;
  border: ${({ $isActive, theme }) => $isActive ? `1px dashed ${theme.colors.color_Gray_05}` : 'none'};
`;

const DropSpaceLast = styled.div`
  width: 100%;
  flex: 1;
  min-height: 20px;
`;

export default Indexing;
